import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { X, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAMAGE_TYPES = [
  'Pemisahan Tapak',
  'Meledak',
  'Jalan Kempes',
  'Sobek/Bocor',
  'Kerusakan Dinding',
  'Kerusakan Bead',
  'Tread Habis',
  'Lainnya',
];

export default function DismountTireModal({ truckId, position, currentTire, pastLifetimeKm = 0, onClose, onComplete }) {
  const { profile, user } = useAuth();
  const [step, setStep] = useState('confirm');
  const [reportedSerial, setReportedSerial] = useState('');
  const [formData, setFormData] = useState({
    odometer: '',
    tread_depth: '',
    condition: 'ready',
    damage_types: [],
    notes: '',
    photo_file_name: '',
    dismount_date: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];
  const mountedDateStr = position?.mounted_date
    ? new Date(position.mounted_date).toISOString().split('T')[0]
    : undefined;

  const handleConfirmMatch = (matches) => {
    if (matches) setStep('form'); else setStep('anomaly');
  };

  const flagAnomalyAndContinue = async () => {
    try {
      const { error } = await supabase.from('anomaly_flags').insert([{
        company_id: profile.company_id, truck_id: truckId,
        position_number: position.position_number,
        expected_serial: currentTire.serial_number,
        reported_serial: reportedSerial || 'TIDAK DIKETAHUI',
        user_id: user.id,
      }]);
      if (error) throw error;
      toast.warning('Anomali ditandai. Lanjutkan dengan pelepasan.');
      setStep('form');
    } catch (error) {
      console.error('Error flagging anomaly:', error);
      toast.error('Gagal menandai anomali');
    }
  };

  const toggleDamageType = (type) => {
    setFormData(prev => ({
      ...prev,
      damage_types: prev.damage_types.includes(type)
        ? prev.damage_types.filter(t => t !== type)
        : [...prev.damage_types, type],
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, photo_file_name: file.name });
      toast.info('Foto dipilih (penyimpanan tiruan - nama file disimpan)');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.dismount_date) {
      toast.error('Pilih tanggal pelepasan');
      return;
    }
    if (formData.dismount_date > todayStr) {
      toast.error('Tanggal tidak boleh di masa depan');
      return;
    }
    if (mountedDateStr && formData.dismount_date < mountedDateStr) {
      toast.error(`Tanggal tidak boleh sebelum tanggal pemasangan (${new Date(mountedDateStr).toLocaleDateString('id-ID')})`);
      return;
    }
    const odoNum = parseInt(formData.odometer);
    if (Number.isNaN(odoNum)) {
      toast.error('Odometer pelepasan wajib diisi');
      return;
    }
    if (position?.mounted_km != null && odoNum < position.mounted_km) {
      toast.error(`Odometer pelepasan (${odoNum.toLocaleString('id-ID')} KM) tidak boleh lebih kecil dari odometer pemasangan (${Number(position.mounted_km).toLocaleString('id-ID')} KM). Periksa kembali angkanya.`);
      return;
    }
    setLoading(true);
    try {
      const treadNum = parseFloat(formData.tread_depth);
      const dismountIso = new Date(`${formData.dismount_date}T12:00:00`).toISOString();

      const { data: posData, error: posError } = await supabase
        .from('tire_positions').update({
          dismounted_km: odoNum,
          dismounted_date: dismountIso,
          tread_depth_off: treadNum,
        })
        .eq('truck_id', truckId).eq('position_number', position.position_number)
        .is('dismounted_date', null).select().single();
      if (posError) throw posError;

      let newStatus = 'stock';
      if (formData.condition === 'scrap') newStatus = 'scrap';
      else if (formData.condition === 'repair') newStatus = 'repair';

      const { error: tireError } = await supabase.from('tires').update({ status: newStatus, tread_depth: treadNum }).eq('id', currentTire.id);
      if (tireError) throw tireError;

      if (formData.condition === 'scrap') {
        const cycleKm = posData.mounted_km ? odoNum - posData.mounted_km : 0;
        const totalLifetimeKm = pastLifetimeKm + cycleKm;
        const { error: scrapError } = await supabase.from('scrap_records').insert([{
          tire_id: currentTire.id,
          damage_types: formData.damage_types,
          tread_depth: treadNum,
          km_total: totalLifetimeKm,
          notes: formData.notes,
          photo_url: formData.photo_file_name || null,
          scrapped_at: dismountIso,
        }]);
        if (scrapError) throw scrapError;
      }

      const labelMap = { ready: 'SIAP PAKAI', repair: 'PERBAIKAN', scrap: 'RUSAK' };
      toast.success(`Ban dilepas: ${labelMap[formData.condition]}`);
      onComplete();
    } catch (error) {
      console.error('Error dismounting tire:', error);
      toast.error(error.message || 'Gagal melepas ban');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="dismount-modal">
      <div className="bg-white border border-zinc-300 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
            LEPAS — POS {position.position_number}
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="close-dismount-modal">
            <X size={24} weight="bold" />
          </button>
        </div>

        <div className="p-6">
          {step === 'confirm' && (
            <div data-testid="confirm-step">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">VERIFIKASI BAN SAAT INI</p>
              <div className="bg-zinc-50 border border-zinc-200 p-4 mb-6">
                <p className="text-sm font-mono text-zinc-700 mb-2">Ban di posisi ini adalah:</p>
                <p className="text-2xl font-mono text-zinc-900 mb-1" data-testid="expected-serial">{currentTire.serial_number}</p>
                <p className="text-xs font-mono text-zinc-500">{currentTire.type} | {currentTire.size}</p>
                {pastLifetimeKm > 0 && (
                  <p className="text-xs font-mono text-zinc-600 mt-2">Umur sebelumnya: {pastLifetimeKm.toLocaleString('id-ID')} KM</p>
                )}
              </div>
              <p className="text-sm font-mono text-zinc-700 mb-6">Apakah ini benar?</p>
              <div className="flex gap-3">
                <button onClick={() => handleConfirmMatch(true)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 transition-colors"
                  data-testid="confirm-yes-button">
                  YA, LANJUTKAN
                </button>
                <button onClick={() => handleConfirmMatch(false)}
                  className="flex-1 border border-amber-500 hover:bg-amber-50 text-amber-700 font-mono uppercase text-xs tracking-wider py-3 px-6 transition-colors"
                  data-testid="confirm-no-button">
                  TIDAK, TIDAK COCOK
                </button>
              </div>
            </div>
          )}

          {step === 'anomaly' && (
            <div data-testid="anomaly-step">
              <div className="bg-red-50 border border-red-600 p-4 mb-6 flex items-start gap-3">
                <Warning size={24} weight="bold" className="text-red-600 mt-1 shrink-0" />
                <div>
                  <p className="text-zinc-900 font-mono uppercase text-sm font-bold tracking-wider mb-1">
                    BAN TIDAK COCOK TERDETEKSI
                  </p>
                  <p className="text-zinc-700 font-mono text-xs">
                    Posisi ini menunjukkan <span className="text-zinc-900">{currentTire.serial_number}</span> tetapi Anda menunjukkan ban yang berbeda. Ban mungkin telah diganti tanpa dicatat.
                  </p>
                </div>
              </div>
              <div className="mb-6">
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">
                  Nomor Seri Ban Sebenarnya di Posisi (opsional)
                </label>
                <input type="text" value={reportedSerial} onChange={(e) => setReportedSerial(e.target.value)}
                  placeholder="Masukkan nomor seri yang Anda lihat"
                  className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                  data-testid="reported-serial-input" />
              </div>
              <div className="flex gap-3">
                <button onClick={flagAnomalyAndContinue}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 transition-colors"
                  data-testid="flag-anomaly-button">
                  TANDAI & LANJUTKAN
                </button>
                <button onClick={onClose}
                  className="border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-mono uppercase text-xs tracking-wider py-3 px-6 transition-colors"
                  data-testid="cancel-anomaly-button">
                  BATAL
                </button>
              </div>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} data-testid="dismount-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Odometer (KM) *</label>
                  <input type="number" value={formData.odometer}
                    onChange={(e) => setFormData({ ...formData, odometer: e.target.value })}
                    min={position?.mounted_km ?? undefined}
                    placeholder={position?.mounted_km != null ? `≥ ${Number(position.mounted_km).toLocaleString('id-ID')}` : undefined}
                    className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                    required data-testid="dismount-odometer-input" />
                  {position?.mounted_km != null && (
                    <p className="text-[10px] font-mono text-zinc-500 mt-1">
                      Odometer saat pasang: <span className="text-zinc-900 font-bold">{Number(position.mounted_km).toLocaleString('id-ID')} KM</span>. Angka pelepasan harus ≥ angka ini.
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Kedalaman Tapak (mm) *</label>
                  <input type="number" step="0.1" value={formData.tread_depth}
                    onChange={(e) => setFormData({ ...formData, tread_depth: e.target.value })}
                    className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                    required data-testid="dismount-tread-input" />
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">
                  Tanggal Pelepasan *
                </label>
                <input type="date" value={formData.dismount_date}
                  onChange={(e) => setFormData({ ...formData, dismount_date: e.target.value })}
                  max={todayStr}
                  min={mountedDateStr}
                  className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                  required data-testid="dismount-date-input" />
                <p className="text-[10px] font-mono text-zinc-500 mt-1">
                  Default hari ini. Tanggal masa depan tidak diizinkan.
                  {mountedDateStr && ` Minimum: ${new Date(mountedDateStr).toLocaleDateString('id-ID')} (tanggal pemasangan).`}
                </p>
              </div>

              <div className="mb-4">
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Kondisi *</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'ready', label: 'Siap Pakai' },
                    { value: 'repair', label: 'Perbaikan' },
                    { value: 'scrap', label: 'Rusak' },
                  ].map((opt) => (
                    <button key={opt.value} type="button" onClick={() => setFormData({ ...formData, condition: opt.value })}
                      className={`p-3 font-mono uppercase text-xs tracking-wider transition-colors ${
                        formData.condition === opt.value ? 'bg-red-600 text-white' : 'border border-zinc-300 text-zinc-700 hover:bg-zinc-100'
                      }`}
                      data-testid={`condition-${opt.value}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {formData.condition === 'scrap' && (
                <div className="mb-4">
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">
                    Jenis Kerusakan (pilih semua yang berlaku) *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {DAMAGE_TYPES.map((type) => (
                      <button key={type} type="button" onClick={() => toggleDamageType(type)}
                        className={`p-3 font-mono uppercase text-xs tracking-wider transition-colors text-left ${
                          formData.damage_types.includes(type) ? 'bg-red-600 text-white' : 'border border-zinc-300 text-zinc-700 hover:bg-zinc-100'
                        }`}
                        data-testid={`damage-${type.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">
                  Foto (opsional, penyimpanan tiruan)
                </label>
                <input type="file" accept="image/*" onChange={handlePhotoChange}
                  className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:outline-none file:bg-zinc-100 file:text-zinc-900 file:border-0 file:px-3 file:py-1 file:mr-3 file:font-mono file:uppercase file:text-xs"
                  data-testid="photo-input" />
                {formData.photo_file_name && (
                  <p className="text-xs font-mono text-zinc-600 mt-2">Dipilih: {formData.photo_file_name}</p>
                )}
              </div>

              <div className="mb-6">
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Catatan</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                  data-testid="notes-input" />
              </div>

              <div className="flex gap-3">
                <button type="submit" disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 transition-colors disabled:opacity-50"
                  data-testid="submit-dismount-button">
                  {loading ? 'MEMPROSES...' : 'KONFIRMASI LEPAS'}
                </button>
                <button type="button" onClick={onClose}
                  className="border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-mono uppercase text-xs tracking-wider py-3 px-6 transition-colors"
                  data-testid="cancel-dismount-button">
                  BATAL
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
